// UI v2: Figma Component (aMKcWb3WyLMnu7FYfTf9W3) の Semantic / Layout / Text Styles。
// get_variable_defs で確認した値。Primitive / Brand は画面から直接参照しない。
export const semanticColors = {
  "text/primary": "#16202E",
  "text/secondary": "#535F73",
  "text/disabled": "#ADB8C6",
  "text/inverse": "#FFFFFF",
  "text/link": "#3E72CC",
  "text/error": "#D10A0F",
  "text/heading": "#303F68",
  "text/placeholder": "#535F73",
  "surface/base": "#F3F6FA",
  "surface/card": "#FFFFFF",
  "surface/subtle": "#FAFBFD",
  "surface/inverse": "#16202E",
  "surface/trust": "#303F68",
  "surface/overlay": "#16202E99",
  "surface/hero-start": "#445C91",
  "border/default": "#E4E9F0",
  "border/strong": "#CBD3DE",
  "border/focus": "#3E72CC",
  "action/primary": "#3E72CC",
  "action/primary-hover": "#3462B2",
  "action/primary-pressed": "#2A5296",
  "action/primary-disabled": "#ADB8C6",
  "action/primary-grad-end": "#3462B2",
  "action/secondary": "#354052",
  "action/secondary-hover": "#16202E",
  "action/trust": "#303F68",
  "action/trust-hover": "#273254",
  "action/trust-grad-start": "#455583",
  "action/trust-pressed-grad-end": "#273254",
  "state/selected-bg": "#DCEAFA",
  // AtomsのText押下色（Figmaでは未バインド）とBadgeのgreen/700を用途名で参照する。
  "state/text-pressed-bg": "#E9EDF3",
  "field/bg": "#E4E9F0",
  "field/bg-disabled": "#F3F6FA",
  "status/success": "#2C9D45",
  "status/success-bg": "#EDFFEB",
  "status/success-text": "#257241",
  "status/error": "#D10A0F",
  "status/error-bg": "#FFF2F4",
  "status/warning": "#F7B600",
  "status/warning-bg": "#FFF9F0",
  "status/info": "#3E72CC",
  "status/info-bg": "#DCEAFA",
  "accent/mark": "#F2801E",
  "accent/bg": "#FDEEE0",
  "accent/text": "#8A4407",
  "icon/default": "#8E9AAB",
  "icon/muted": "#ADB8C6",
  "icon/strong": "#535F73",
  "icon/primary": "#3E72CC",
  "icon/inverse": "#FFFFFF",
  "rank/gold": "#C49A16",
  "rank/silver": "#8E9AAB",
  "rank/bronze": "#C08A4E",
  "chart/baseline": "#CBD3DE",
  "chart/income-1": "#3E72CC",
  "chart/income-2": "#7BA7E2",
  "chart/expense-1": "#DA6F10",
  "chart/expense-2": "#F2801E",
  "chart/expense-3": "#F5974C",
  "chart/expense-4": "#F9C194",
  "chart/other": "#ADB8C6",
  // 対応可能種別（区分／一棟／海外不動産）の固定色と、得意な領域タグの面（Trello #245）。
  // Figma の Semantic には未追加で、Primitive の blue/700・indigo/700・green/700・gray/200 と同じ値
  "category/condo": "#3E72CC",
  "category/building": "#303F68",
  "category/overseas": "#257241",
  "tag/strength-bg": "#E4E9F0",
} as const;

export const layoutTokens = {
  breakpoints: { xs: 0, sm: 600, md: 768, lg: 1280, xl: 1920 },
  space: { 0: 0, 4: 4, 8: 8, 12: 12, 16: 16, 20: 20, 24: 24, 28: 28, 32: 32, 36: 36, 40: 40, 48: 48, 64: 64 },
  radius: { sm: 4, md: 8, lg: 16, full: 9999 },
} as const;

export const fontFamilies = {
  body: "\"Noto Sans JP\", sans-serif",
  number: "Inter, \"Noto Sans JP\", sans-serif",
} as const;

const notoStyle = (fontSize: number, fontWeight: 400 | 500 | 700, lineHeight: number) => ({
  fontFamily: fontFamilies.body,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing: "0em",
});

const numberStyle = (fontSize: number, letterSpacing: string) => ({
  fontFamily: fontFamilies.number,
  fontSize,
  fontWeight: 600,
  lineHeight: 1,
  letterSpacing,
});

// 既存の theme.typography のサイズは変えず、フローのPRでこのスタイルを適用する。
export const textStyles = {
  "PC/Heading/H1": notoStyle(32, 700, 1.3),
  "PC/Heading/H2": notoStyle(24, 700, 1.3),
  "PC/Heading/H3": notoStyle(20, 700, 1.3),
  "PC/Heading/H4": notoStyle(16, 700, 1.3),
  "PC/Heading/H5": notoStyle(14, 700, 1.3),
  "PC/Label/H6": notoStyle(12, 400, 1.3),
  "PC/Body/XLarge": notoStyle(20, 400, 1.5),
  "PC/Body/Large": notoStyle(16, 400, 1.5),
  "PC/Body/Small": notoStyle(14, 400, 1.5),
  "PC/Caption": notoStyle(12, 400, 1.5),
  "PC/Button/L": notoStyle(16, 500, 1.3),
  "PC/Button/M": notoStyle(14, 500, 1.3),
  "PC/Button/S": notoStyle(12, 500, 1.3),
  "SP/Heading/H1": notoStyle(24, 700, 1.3),
  "SP/Heading/H2": notoStyle(20, 700, 1.3),
  "SP/Heading/H3": notoStyle(16, 700, 1.3),
  "SP/Heading/H4": notoStyle(14, 700, 1.3),
  "SP/Heading/H5": notoStyle(12, 700, 1.3),
  "SP/Label/H6": notoStyle(10, 400, 1.3),
  "SP/Body/XLarge": notoStyle(16, 400, 1.5),
  "SP/Body/Large": notoStyle(14, 400, 1.5),
  "SP/Body/Small": notoStyle(12, 400, 1.5),
  "SP/Caption": notoStyle(10, 400, 1.5),
  "SP/Button/L": notoStyle(14, 500, 1.3),
  "SP/Button/M": notoStyle(14, 500, 1.3),
  "SP/Button/S": notoStyle(12, 500, 1.3),
  "Number/XL": numberStyle(32, "-0.03em"),
  "Number/L": numberStyle(24, "-0.025em"),
  "Number/M": numberStyle(20, "-0.02em"),
  "Number/S": numberStyle(16, "-0.01em"),
  "Number/XS": numberStyle(14, "0em"),
  "Number/XXS": numberStyle(12, "0em"),
  "Number/Caption": { ...numberStyle(12, "0em"), fontWeight: 400, lineHeight: 1.5 },
} as const;

export const tabularNumberStyle = { fontFeatureSettings: "\"tnum\"" } as const;

// LESS と MUI の参照元を共通化する。値を .less 側へ複製しない。
export const cssCustomProperties: Record<string, string> = {
  "--font-body": fontFamilies.body,
  "--font-number": fontFamilies.number,
};
(Object.keys(semanticColors) as Array<keyof typeof semanticColors>).forEach((name) => {
  cssCustomProperties[`--color-${name.replace(/\//g, "-")}`] = semanticColors[name];
});
Object.keys(layoutTokens.space).forEach((size) => {
  cssCustomProperties[`--space-${size}`] = `${layoutTokens.space[Number(size) as keyof typeof layoutTokens.space]}px`;
});
(Object.keys(layoutTokens.radius) as Array<keyof typeof layoutTokens.radius>).forEach((name) => {
  cssCustomProperties[`--radius-${name}`] = `${layoutTokens.radius[name]}px`;
});

// 移行中の互換値。既存の警告・オレンジCTA・多系列グラフは用途を確認するPRで移す。
// 新規画面には使わず、v2 の実装は semanticColors / layoutTokens を参照する。
export const legacyColors = {
  warning: { main: "#F87943", light: "#FFF5F2" },
  orange: {
    50: "#FFF5F2",
    100: "#FED0BF",
    200: "#FDAD8E",
    300: "#FB9064",
    400: "#F87943",
    500: "#F4682A",
    600: "#F05B18",
    700: "#EA510C",
    800: "#E44B04",
    900: "#DE4600",
  },
  chart: {
    income: { primary: "#103894", darker: "#1B42A0", dark: "#2D54B6" },
    expense: {
      primary: "#E25213",
      darker: "#EC6C17",
      dark: "#F98B1A",
      light: "#FEA532",
      lighter: "#FFBD45",
      lightest: "#FFD662",
    },
  },
  chartSeries: {
    income: ["#2476E4", "#4399F9", "#74C4FD"],
    expense: ["#E44B04", "#EA510C", "#F05B18", "#F4682A", "#F87943", "#FB9064", "#FDAD8E", "#FED0BF", "#FFF5F2"],
  },
  chartScheme: {
    PrBlue: "#57ADFB",
    Strawberry: "#F6636B",
    Orange: "#F05B18",
    Mint: "#4399F9",
    LightGray: "#F5F5F5",
    Emerald: "#ADEDA7",
    Black: "#616161",
  },
  red: {
    50: semanticColors["status/error-bg"],
    100: "#FDBFC5",
    200: "#FB8D95",
    300: "#F6636B",
    400: "#F14149",
    500: "#E8282F",
    600: "#DE161C",
    700: semanticColors["status/error"],
    800: "#C40307",
    900: "#B80003",
  },
  green: {
    50: semanticColors["status/success-bg"],
    100: "#ADEDA7",
    200: "#70DA6C",
    300: "#3CC83C",
    400: "#32B341",
    500: semanticColors["status/success"],
    600: "#288844",
    700: "#257241",
    800: "#215D3A",
    900: "#1D4731",
  },
  yellow: {
    50: semanticColors["status/warning-bg"],
    100: "#FFE3A8",
    200: "#FFD463",
    300: "#FFCE26",
    400: "#FCBF04",
    500: semanticColors["status/warning"],
    600: "#F1AD00",
    700: "#EBA300",
    800: "#E49900",
    900: "#DE9000",
  },
} as const;
